import { FeedsClient, type Feed } from "@stream-io/feeds-react-sdk";
import type { StoriesTokenData } from "Api/Queries/Stories/useStoriesTokenQuery";
import { ConnectSupersededError } from "Utils/ConnectSupersededError";

/**
 * Stream Feeds V3 built-in timeline feed group configured for stories — the client-side
 * counterpart of StoryFeedConstants.TimelineFeedGroup on the backend.
 */
const STORIES_TIMELINE_FEED_GROUP = "stories";

/**
 * Thrown when a newer connect (persona switch, logout, query cancellation) superseded this one,
 * or when a token refresh finds the active persona changed underneath the connection.
 */
export class StoriesConnectSupersededError extends ConnectSupersededError {
  constructor(message = "Stories connect superseded") {
    super(message);
    this.name = "StoriesConnectSupersededError";
  }
}

type ConnectedStoriesFeed = {
  /** apiKey + Stream user id — a different pair means a different persona and forces a reconnect. */
  key: string;
  /** The token the timeline was last read under — see synchronizeStoriesFeedForToken. */
  token: string;
  client: FeedsClient;
  feedPromise: Promise<Feed>;
};

let current: ConnectedStoriesFeed | null = null;

const connectionKey = (storiesToken: StoriesTokenData): string =>
  `${storiesToken.apiKey}:${storiesToken.userId}`;

/**
 * Connects the Stream Feeds client (once per persona) and opens the caller's stories timeline
 * with watch: true, so the SDK keeps the feed state live over WebSocket. Module-level singleton
 * for the same reason as the chat client: effects can run twice (StrictMode) and remounts must
 * reuse the existing connection instead of re-connecting per render.
 *
 * The token is supplied as a PROVIDER, not a string: the SDK only refreshes expired tokens
 * (WS TOKEN_EXPIRED reconnect, REST 401 retry) when the token is non-static, and it also calls
 * the provider for the initial connect — so every token in play is fresh at call time.
 *
 * Every await in the success path re-checks that this connect is still the current one; a
 * logout or persona switch that lands mid-connect aborts it and disconnects the client instead
 * of leaving an untracked, connected WebSocket behind.
 */
export const connectStoriesFeed = (
  storiesToken: StoriesTokenData,
  tokenProvider: () => Promise<string>,
  signal?: AbortSignal,
): Promise<Feed> => {
  const key = connectionKey(storiesToken);
  if (current?.key === key) {
    return current.feedPromise;
  }

  const previous = current;
  const client = new FeedsClient(storiesToken.apiKey);
  const feedPromise = (async () => {
    // Only valid after an await: the IIFE body runs synchronously up to its first await, i.e.
    // BEFORE `current = { key, client, feedPromise }` below — a synchronous check would always
    // see a foreign `current` and abort every fresh connect.
    const assertNotSuperseded = () => {
      if (current?.client !== client || signal?.aborted) {
        throw new StoriesConnectSupersededError();
      }
    };

    try {
      // Persona switch: drop the previous identity's connection first.
      if (previous) {
        await previous.client.disconnectUser().catch(() => undefined);
        assertNotSuperseded();
      }
      await client.connectUser({ id: storiesToken.userId }, tokenProvider);
      assertNotSuperseded();
      const feed = client.feed(
        STORIES_TIMELINE_FEED_GROUP,
        storiesToken.userId,
      );
      await feed.getOrCreate({ watch: true });
      assertNotSuperseded();
      return feed;
    } catch (error) {
      // Do not cache a failed connection — the next call retries from scratch. On supersession
      // this disconnect is the zombie kill: the client may have fully connected in the meantime.
      if (current?.client === client) {
        current = null;
      }
      await client.disconnectUser().catch(() => undefined);
      throw error;
    }
  })();

  current = { key, token: storiesToken.token, client, feedPromise };
  return feedPromise;
};

/**
 * Re-reads the live timeline when the backend issued a NEW stories token for the same persona.
 * The token endpoint is where the backend ensures the viewer's timeline follows (ADR-0002), so a
 * fresh token means the follow set may have changed — and a watched feed only learns about new
 * activities on feeds it already follows, never about new follow edges. Deduplicated on the token
 * string, so StrictMode double effects and remounts with a cached token are no-ops; the SDK's own
 * expiry refreshes bypass the query and never reach here. Best-effort: a failed re-read keeps the
 * current state and the next token tries again.
 */
export const synchronizeStoriesFeedForToken = async (
  storiesToken: StoriesTokenData,
): Promise<void> => {
  const connection = current;
  if (
    !connection ||
    connection.key !== connectionKey(storiesToken) ||
    connection.token === storiesToken.token
  ) {
    return;
  }
  connection.token = storiesToken.token;
  const feed = await connection.feedPromise.catch(() => null);
  // A persona switch or logout that landed while awaiting owns the connection now.
  if (!feed || current !== connection) {
    return;
  }
  await feed.synchronize().catch(() => undefined);
};

export const disconnectStoriesFeed = async (): Promise<void> => {
  if (!current) {
    return;
  }
  const { client } = current;
  current = null;
  await client.disconnectUser().catch(() => undefined);
};
